import { useFormik } from "formik";
import { AiOutlineInfoCircle } from "react-icons/ai";
import * as yup from "yup";
import dayjs from "dayjs";
import Breadcrumb from "../../components/Breadcrumb";
import { useToast } from "../../components/Toast/toastHook";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_NOTIFICATION_AGENTS,
  SEND_NOTIFICATION_OFFICE,
} from "../../graphql/notificationQueries";
import { GET_COMPANY_OFFICES } from "../../graphql/companyQueries";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import CompanyNotificationForm from "../../components/companies/CompanyNotificationForm";
import NotificationsHistory, {
  useNotificationsHistory,
} from "../admin/NotificationsHistory";
import SentNotificationsTable from "../admin/SentNotificationsTable";
import NotificationsTabs from "../../components/NotificationsTabs";
import { getStoredRoleName } from "../../utils/notificationNav";

const AdminNotifications = () => {
  const toast = useToast(4000);
  const { companyId } = useParams();
  const [tab, setTab] = useState("received");
  const canSeeSent = getStoredRoleName() !== "AGENT";
  const history = useNotificationsHistory();

  const [sendNotificationOffice, { data, loading, error }] = useMutation(
    SEND_NOTIFICATION_OFFICE,
    {
      // Refresh the Sent list so the new notification appears without a reload.
      refetchQueries: ["getSentNotifications"],
      onCompleted(result) {
        toast("success", result?.sendNotificationOffice || "Notification sent successfully!");
        // Land the admin on what they just sent.
        if (canSeeSent) setTab("sent");
      },
      onError(e) {
        console.error(e);
        toast("error", e?.message ?? "Something went wrong!");
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      loading: false,
      toEveryOne: false,
      sendMode: "now",
      scheduledDate: null,
      scheduledTime: null,
      search: {
        office: "",
        agent: "",
      },
      isOpen: {
        office: false,
        agent: false,
      },
      selectedItems: { office: [], agent: [] },
      companies: [],
      users: [],
    },
    validationSchema: yup.object().shape({
      title: yup.string().required("Title cannot be empty").max(100),
      description: yup.string().required("Description is Required").max(500),
    }),
    onSubmit: async (values, helper) => {
      let scheduledAtISO = null;
      if (values.sendMode === "schedule") {
        if (!values.scheduledDate || !values.scheduledTime) {
          formik.setFieldError(
            "scheduledAt",
            "Pick a date and time to schedule this notification."
          );
          return;
        }
        const combined = values.scheduledDate
          .hour(values.scheduledTime.hour())
          .minute(values.scheduledTime.minute())
          .second(0)
          .millisecond(0);
        if (combined.isBefore(dayjs())) {
          formik.setFieldError("scheduledAt", "Pick a time in the future.");
          return;
        }
        scheduledAtISO = combined.toISOString();
      }

      formik.setFieldValue("loading", true);
      let notificationOfficeInput = {
        body: values.description,
        title: values.title,
      };
      if (values.selectedItems.office.length > 0) {
        notificationOfficeInput = {
          ...notificationOfficeInput,
          officeIDs: values.selectedItems.office,
        };
      }
      if (values.selectedItems.agent.length > 0) {
        notificationOfficeInput = {
          ...notificationOfficeInput,
          agentIDs: values.selectedItems.agent,
        };
      }
      if (scheduledAtISO) {
        notificationOfficeInput = {
          ...notificationOfficeInput,
          scheduledAt: scheduledAtISO,
        };
      }
      sendNotificationOffice({
        variables: {
          notificationOfficeInput,
        },
      });
      helper.resetForm();
      formik.setFieldValue("loading", false);
      populateData();
    },
  });

  const companyRes = useQuery(GET_COMPANY_OFFICES, {
    variables: {
      companyId: companyId,
    },
  });

  const userRes = useQuery(GET_NOTIFICATION_AGENTS, {
    variables: {
      companyId: companyId,
    },
  });

  const dropdownRef = useRef(null);

  const populateData = () => {
    if (companyRes.data) {
      formik.setFieldValue("companies", companyRes.data.getCompanyOffice);
    }
    if (userRes.data) {
      formik.setFieldValue("users", userRes.data.getNotificationAgents);
    }
  };

  useEffect(() => {
    if (companyRes.refetch) {
      companyRes.refetch();
    }
    if (userRes.refetch) {
      userRes.refetch();
    }
    populateData();
  }, [companyRes.data, userRes.data]);

  useEffect(() => {
    const handleBodyClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        formik.setFieldValue("isOpen.office", false);
        formik.setFieldValue("isOpen.agent", false);
      }
    };
    document.addEventListener("mousedown", handleBodyClick);

    return () => {
      document.removeEventListener("mousedown", handleBodyClick);
    };
  }, []);

  return (
    <div id="Notifications">
      <Breadcrumb pageName="Notifications" />
      <NotificationsTabs
        tab={tab}
        onChange={setTab}
        unreadCount={history.unreadCount}
        showSent={canSeeSent}
      />
      {tab === "compose" && (
        <>
          <div className="flex gap-2 items-start md:items-center flex-col md:flex-row text-slate-400">
            <AiOutlineInfoCircle size="18" />
            <p className="text-sm">
              Facilitates the broadcasting of notifications to all registered
              agents of the mobile application.
            </p>
          </div>
          <CompanyNotificationForm formik={formik} dropdownRef={dropdownRef} />
        </>
      )}
      {tab === "received" && <NotificationsHistory history={history} />}
      {tab === "sent" && canSeeSent && <SentNotificationsTable />}
    </div>
  );
};

export default AdminNotifications;
